import { useCallback, useMemo, useState } from 'react';
import { Image, Select, message } from 'antd';
import { DeleteOutlined, SendOutlined, DownOutlined, RightOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import AppModal from '@/pages/inside/tenant/AppModal';
import { Button } from '@/components/ui/Button';
import RichTextEditor from '@/components/ui/RichTextEditor';
import Markdown from '@/components/ui/Markdown';
import PhaseDescriptionAttachments from '@/components/module/projects/milestone_tab/PhaseDescriptionAttachments';
import PhaseDescriptionCollapsible from '@/components/module/projects/milestone_tab/PhaseDescriptionCollapsible';
import { GlobalImagePreviewStyles } from '@/components/ui/GlobalImagePreviewStyles';
import { CreateRoleModalBody, CreateRoleModalFooter } from '@/pages/inside/tenant/style';
import { BacklogTaskBanner } from '@/components/sprint/BacklogTaskBanner';
import { isLikelyImageAttachmentUrl, resolveAttachmentUrl, stripHtmlPreview } from '@hooks/projects/projectHelpers';
import { isTaskOnDevBacklog } from '@hooks/projects/projectHelpers';

/** Section headings: Submission Description, Submitted Tasks, Description (read-only), etc. */
const submissionSectionHeadingStyle = {
    display: 'block',
    marginBottom: 8,
    color: '#71717A',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.25,
};

/** Full-bleed divider vs modal body padding (~16px). */
const fullBleedDividerStyle = {
    height: 1,
    background: '#E0E0E0',
    border: 'none',
    marginLeft: -16,
    marginRight: -16,
    width: 'calc(100% + 32px)',
    flexShrink: 0,
};

const submittedTasksOuterStyle = {
    border: '1px solid #E0E0E0',
    borderRadius: 10,
    overflow: 'hidden',
    background: '#fff',
};

const taskCardStyle = {
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    border: '1px solid #E1DFE7',
    borderRadius: 10,
    background: '#F5F5F5',
    padding: 16,
    boxSizing: 'border-box',
};

const taskTidStyle = {
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1.25,
    color: 'inherit',
    margin: 0,
};

const taskDescStyle = {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: 1.45,
    color: '#767676',
    margin: 0,
};

/** Hover: delete control only — no dimming overlay (task fill stays #F5F5F5). */
const SubmissionTaskRow = styled.div`
    position: relative;
    border-radius: 10px;
    isolation: isolate;

    .submission-task-delete {
        position: absolute;
        top: 8px;
        right: 8px;
        z-index: 1;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        padding: 0;
        border: none;
        border-radius: 6px;
        background: rgba(255, 255, 255, 0.96);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
        color: rgba(0, 0, 0, 0.55);
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s ease, color 0.15s ease, background 0.15s ease;
        pointer-events: none;
    }

    .submission-task-delete:hover {
        color: rgba(0, 0, 0, 0.88);
        background: #fff;
    }

    &:hover .submission-task-delete {
        opacity: 1;
        pointer-events: auto;
    }
`;

/**
 * Shared submission flow: optional rich-text **note** (blank for client flow), optional **Description** as plain text
 * from API below the editor, then task list + submit.
 */
export default function ProjectSubmissionModal({
    open,
    onCancel,
    /** 'qa' | 'pmo' | 'submitToClient' — layout + optional blocks */
    variant = 'qa',
    /** Kept for callers / analytics; not shown in the modal header. */
    sidLabel: _sidLabel = 'SID-1',
    /** Modal header (e.g. Submission 1) */
    submissionTitle = 'Submission 1',
    /** Primary footer action label — same for QA, PMO, and client flows */
    submitButtonLabel = 'Submit',
    /** Controlled rich-text HTML for submission note (same field name for API compatibility) */
    descriptionHtml = '',
    onDescriptionChange,
    /** Optional: persist description (e.g. toast) */
    onSaveDescription,
    /** When false, description is only submitted with the primary action (no separate Save). */
    showSaveButton = false,
    /**
     * Submission description from the API (HTML or plain). Shown only as plain text in the **Description**
     * block below the note editor — not as markdown. Omit to hide that block (e.g. Sprints → QA).
     */
    submissionDescriptionRaw,
    /** Optional block before the editor (e.g. destination selector chip). */
    beforeDescription = null,
    /** Optional block after the editor (e.g. attachment chips). */
    afterDescription = null,
    /** When false, hide file attach in the editor (same pattern as task description). */
    isAttachment = true,
    /** When false, hide the link tool. */
    isLink = true,
    /** When false, disable @-mentions. */
    isMention = true,
    /** Lists for @-mention (e.g. project assignees, milestones, sprints). */
    mentionUsers = [],
    mentionMilestones = [],
    mentionSprints = [],
    /** Extra props forwarded to RichTextEditor (e.g. showAttachButton, onFilesSelected). */
    richTextEditorProps = {},
    /** Rows with display id, title, and payload for click */
    taskRows = [],
    /** (payload) => void — payload includes sprint/task when available */
    onTaskRowClick,
    onRemoveTask,
    onSubmit,
    submitPending = false,
    submitDisabled = false,
    submitPermissionDenied = false,
    emptyTasksMessage = 'Nothing selected yet.',
    /** `submitToClient` only: show “Send this submission to” target selector (hidden by default). */
    showSendTargetSelect = false,
    /** Label above the rich-text note (matches product copy: “Submission Description”). */
    descriptionSectionLabel = 'Submission Description',
    /** Read-only: hide editor, footer submit, show notes + optional link/attachment lists. */
    viewOnly = false,
    /** Normalized `{ url, title }[]` for view-only modal (submission links). */
    viewLinks = [],
    /** Normalized `{ url, title }[]` for view-only modal (submission attachments). */
    viewAttachments = [],
    /** Optional client-provided attachments after submit (view-only). */
    viewClientAttachments = [],
    /**
     * Rich view-only sections (e.g. QA submission + PMO submission + client notes), each like:
     * { key, label, submittedAt, submittedBy, contentMarkdown, attachmentItems }
     * where attachmentItems is `{ type, url, title }[]`.
     */
    detailSections = [],
    /** AppModal width preset — e.g. `md` to match Create sprint modal. */
    modalSize = 'lg',
    /** Optional override for modal footer (e.g. client approve/reject actions). */
    footerOverride,
    /**
     * Override the submit button text. When provided, takes precedence over `submitButtonLabel`.
     * Useful for dynamic labels like "Submit to QA" / "Submit to PMO".
     */
    submitLabel,
}) {
    const handleSaveDescription = useCallback(() => {
        if (typeof onSaveDescription === 'function') {
            onSaveDescription();
        } else {
            message.success('Saved');
        }
    }, [onSaveDescription]);

    const canEditDescription = typeof onDescriptionChange === 'function';
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [previewImageSrc, setPreviewImageSrc] = useState('');

    const notePlaceholder =
        variant === 'submitToClient'
            ? 'Write your note for the client…'
            : 'Add a submission note for reviewers…';

    const submissionDescriptionPlain = useMemo(() => {
        if (submissionDescriptionRaw === undefined) return null;
        if (viewOnly) {
            const raw = typeof submissionDescriptionRaw === 'string' ? submissionDescriptionRaw : '';
            const t = raw
                .replace(/<[^>]+>/g, '')
                .replace(/&nbsp;/g, ' ')
                .trim();
            return t || '—';
        }
        const t = stripHtmlPreview(submissionDescriptionRaw, 0).trim();
        return t || '—';
    }, [submissionDescriptionRaw, viewOnly]);

    const title = (
        <span
            style={{
                fontSize: 18,
                fontWeight: 600,
                lineHeight: 1.25,
                color: 'var(--ant-color-text, rgba(0,0,0,0.88))',
                textDecoration: 'none',
                border: 'none',
            }}
        >
            {submissionTitle}
        </span>
    );

    const handleMarkdownLinkClick = useCallback(({ event, href, isImage }) => {
        const resolved = resolveAttachmentUrl(href);
        if (!resolved) return;
        if (isImage || isLikelyImageAttachmentUrl(resolved)) {
            event?.preventDefault?.();
            event?.stopPropagation?.();
            setPreviewImageSrc(resolved);
        }
    }, []);

    const viewOnlyLinkList = (label, items) => {
        if (!items?.length) return null;
        return (
            <div style={{ marginBottom: 16 }}>
                <span style={submissionSectionHeadingStyle}>{label}</span>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 14, lineHeight: 1.5 }}>
                    {items.map((item, i) => (
                        <li key={`${item.url}-${i}`}>
                            <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    const resolved = resolveAttachmentUrl(item.url);
                                    if (!resolved || !isLikelyImageAttachmentUrl(resolved)) return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setPreviewImageSrc(resolved);
                                }}
                            >
                                {item.title || item.url}
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        );
    };

    const detailSectionsBody = useMemo(() => {
        if (!Array.isArray(detailSections) || detailSections.length === 0) return null;
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {detailSections.map((sec, idx) => {
                    const label = sec?.label || 'Details';
                    const source = typeof sec?.contentMarkdown === 'string' ? sec.contentMarkdown.trim() : '';
                    const attachmentItems = Array.isArray(sec?.attachmentItems) ? sec.attachmentItems : [];
                    return (
                        <div key={sec?.key ?? `${label}-${idx}`}>
                            <span style={submissionSectionHeadingStyle}>{label}</span>
                            <div style={{ height: 8 }} />
                            {source ? (
                                <PhaseDescriptionCollapsible
                                    source={source}
                                    resetKey={`${sec?.key ?? idx}`}
                                    attachmentItems={attachmentItems}
                                    onLinkClick={handleMarkdownLinkClick}
                                />
                            ) : (
                                <p style={{ margin: 0, fontSize: 14, color: 'var(--ant-color-text, rgba(0,0,0,0.88))' }}>—</p>
                            )}
                            {attachmentItems.length > 0 ? <PhaseDescriptionAttachments items={attachmentItems} /> : null}
                            {idx !== detailSections.length - 1 ? (
                                <div style={{ ...fullBleedDividerStyle, marginTop: 16, marginBottom: 0 }} />
                            ) : null}
                        </div>
                    );
                })}
            </div>
        );
    }, [detailSections, handleMarkdownLinkClick]);

    const detailsBlock = useMemo(() => {
        if (!detailSectionsBody) return null;
        const open = viewOnly ? true : detailsOpen;
        return (
            <div style={{ marginBottom: 16 }}>
                {!viewOnly ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 12,
                            width: '100%',
                        }}
                    >
                        <span style={{ ...submissionSectionHeadingStyle, marginBottom: 0 }}>Submission details</span>
                        <button
                            type="button"
                            onClick={() => setDetailsOpen((s) => !s)}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 6,
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600,
                                color: 'var(--ant-color-primary, #7c3aed)',
                                textDecoration: 'underline',
                                textUnderlineOffset: 2,
                            }}
                        >
                            {open ? 'Hide' : 'View'}
                            {open ? <DownOutlined style={{ fontSize: 12 }} /> : <RightOutlined style={{ fontSize: 12 }} />}
                        </button>
                    </div>
                ) : (
                    <span style={{ ...submissionSectionHeadingStyle, marginBottom: 8 }}>Submission details</span>
                )}
                <AnimatePresence initial={false}>
                    {open ? (
                        <motion.div
                            key="submission-details"
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.26, ease: [0.4, 0, 0.2, 1] }}
                            style={{ overflow: 'hidden' }}
                        >
                            <div style={{ marginTop: viewOnly ? 0 : 10 }}>{detailSectionsBody}</div>
                        </motion.div>
                    ) : null}
                </AnimatePresence>
            </div>
        );
    }, [detailSectionsBody, detailsOpen, viewOnly]);

    const footerSlot = footerOverride ? (
        footerOverride
    ) : viewOnly ? (
        <CreateRoleModalFooter $inFooterSlot>
            <Button $primary={false} $size="14px" onClick={onCancel}>
                Close
            </Button>
        </CreateRoleModalFooter>
    ) : (
        <CreateRoleModalFooter $inFooterSlot>
            <Button $primary={false} $size="14px" onClick={onCancel}>
                Cancel
            </Button>
            <Button
                $primary
                $blackOnLight
                $size="14px"
                icon={<SendOutlined />}
                loading={submitPending}
                disabled={submitDisabled}
                permissionDenied={submitPermissionDenied}
                onClick={onSubmit}
            >
                {submitLabel ?? submitButtonLabel}
            </Button>
        </CreateRoleModalFooter>
    );

    return (
        <AppModal
            size={modalSize}
            title={title}
            open={open}
            onCancel={onCancel}
            classNames={{ header: 'submission-modal__header' }}
            styles={{
                header: {
                    flexShrink: 0,
                    borderBottom: 'none',
                    border: 'none',
                    boxShadow: 'none',
                },
            }}
            footer={footerSlot}
        >
            <GlobalImagePreviewStyles />
            <CreateRoleModalBody $flush $noMaxHeight>
                {!viewOnly && beforeDescription}
                {!viewOnly && canEditDescription && (
                    <>
                        <span style={submissionSectionHeadingStyle}>{descriptionSectionLabel}</span>
                        <div style={{ marginBottom: 12 }}>
                            <RichTextEditor
                                value={descriptionHtml}
                                onChange={onDescriptionChange}
                                placeholder={notePlaceholder}
                                submissionNeutralLight
                                isAttachment={isAttachment}
                                isLink={isLink}
                                isMention={isMention}
                                mentionUsers={mentionUsers}
                                mentionMilestones={mentionMilestones}
                                mentionSprints={mentionSprints}
                                {...richTextEditorProps}
                            />
                        </div>
                        {showSaveButton ? (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                <Button type="button" $primary $size="13px" onClick={handleSaveDescription}>
                                    Save
                                </Button>
                            </div>
                        ) : (
                            <div style={{ marginBottom: 8 }} />
                        )}
                    </>
                )}

                {!viewOnly && afterDescription}

                {/* 1) Submission details + View/Hide */}
                {detailsBlock}

                {/* 2) Drawer (divider) */}
                <div style={{ ...fullBleedDividerStyle, marginTop: 8, marginBottom: 16 }} />

                {/* 3) Submitted tasks (no outer container); render cards list */}
                <span style={{ ...submissionSectionHeadingStyle, marginBottom: 8 }}>Selected Tasks</span>
                {taskRows.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#767676', margin: 0 }}>{emptyTasksMessage}</p>
                ) : (
                    <ul
                        style={{
                            margin: 0,
                            padding: 0,
                            listStyle: 'none',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 10,
                        }}
                    >
                        {taskRows.map((row) => {
                            const removable = Boolean(onRemoveTask && row.removeKey != null);
                            const description = row.descriptionMarkdown;
                            const attachmentItems = Array.isArray(row.attachmentItems) ? row.attachmentItems : [];
                            return (
                                <li key={row.key}>
                                    <SubmissionTaskRow>
                                        <button
                                            type="button"
                                            onClick={() => onTaskRowClick?.(row.payload)}
                                            style={{
                                                width: '100%',
                                                textAlign: 'left',
                                                cursor: onTaskRowClick ? 'pointer' : 'default',
                                                border: '1px solid var(--ant-color-border-secondary, #f0f0f0)',
                                                borderRadius: 10,
                                                background: 'var(--ant-color-fill-quaternary, rgba(0,0,0,0.02))',
                                                padding: 12,
                                                boxSizing: 'border-box',
                                                font: 'inherit',
                                                color: 'inherit',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                                                <div style={{ minWidth: 0, flex: 1 }}>
                                                    <div style={{ ...taskTidStyle, marginBottom: 6 }}>{row.taskIdLabel}</div>
                                                    {isTaskOnDevBacklog(row.payload?.task) ? (
                                                        <div style={{ marginBottom: 8 }}>
                                                            <BacklogTaskBanner />
                                                        </div>
                                                    ) : null}
                                                    {row.title ? (
                                                        <div
                                                            style={{
                                                                ...taskDescStyle,
                                                                fontWeight: 600,
                                                                color: 'var(--ant-color-text, rgba(0,0,0,0.88))',
                                                                marginBottom: description ? 6 : 0,
                                                            }}
                                                        >
                                                            {row.title}
                                                        </div>
                                                    ) : null}
                                                    {description ? (
                                                        <Markdown
                                                            source={description}
                                                            variant="compact"
                                                            lineClamp={3}
                                                            linkPrimary
                                                            richInlineAnchors
                                                            attachmentItems={attachmentItems}
                                                            onLinkClick={handleMarkdownLinkClick}
                                                        />
                                                    ) : !row.title ? (
                                                        <div style={taskDescStyle}>—</div>
                                                    ) : null}
                                                </div>
                                                {attachmentItems.length > 0 ? (
                                                    <div style={{ flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                                                        <PhaseDescriptionAttachments items={attachmentItems} style={{ marginTop: 0 }} />
                                                    </div>
                                                ) : null}
                                            </div>
                                        </button>
                                        {removable && (
                                            <button
                                                type="button"
                                                className="submission-task-delete"
                                                aria-label="Remove from submission"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onRemoveTask(row.removeType, row.removeKey);
                                                }}
                                            >
                                                <DeleteOutlined style={{ fontSize: 14 }} />
                                            </button>
                                        )}
                                    </SubmissionTaskRow>
                                </li>
                            );
                        })}
                    </ul>
                )}



                {variant === 'submitToClient' && showSendTargetSelect && (
                    <>
                        <span style={{ ...submissionSectionHeadingStyle, marginTop: 20, marginBottom: 8 }}>
                            Send this submission to
                        </span>
                        <Select
                            style={{ width: '100%' }}
                            value="client"
                            disabled
                            options={[{ value: 'client', label: 'Client' }]}
                        />
                    </>
                )}
            </CreateRoleModalBody>
            <Image
                style={{ display: 'none' }}
                src={previewImageSrc || undefined}
                preview={{
                    visible: !!previewImageSrc,
                    src: previewImageSrc || undefined,
                    rootClassName: 'global-image-preview-root',
                    onVisibleChange: (visible) => {
                        if (!visible) setPreviewImageSrc('');
                    },
                }}
            />
        </AppModal>
    );
}
